/**
 * Keeps Embossing Service lines paired 1:1 with a book/set via _embossing_id.
 * Auto-removes orphans (e.g. after cart-drawer remove) and blocks checkout until clean.
 */
(function () {
  const EMBOSSING_TITLE = 'Embossing';
  let purgeInFlight = false;
  let checkoutGateInFlight = false;

  function isEmbossingItem(item) {
    return Boolean(item.product_title && item.product_title.includes(EMBOSSING_TITLE));
  }

  function getEmbossingId(item) {
    return item.properties && item.properties._embossing_id;
  }

  function findOrphanedServices(cart) {
    return cart.items.filter((item) => {
      if (!isEmbossingItem(item)) return false;
      const id = getEmbossingId(item);
      if (!id) return false;
      return !cart.items.some(
        (other) => !isEmbossingItem(other) && getEmbossingId(other) === id
      );
    });
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async function removeLineKeys(keys) {
    if (!keys.length) return null;
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return response.json();
  }

  function refreshCartUI() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'embossing-cart-guard' });
      return;
    }
    // Fallback when pubsub is unavailable (should be rare).
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function showRemovedNotice(orphans) {
    const banner = document.getElementById('embossing-warning-banner-drawer');
    const servicesList = document.getElementById('orphaned-services-list-drawer');
    if (!banner || !servicesList) return;

    const title = banner.querySelector('strong');
    const copy = banner.querySelector('p');
    if (title) title.textContent = 'Removed orphaned embossing';
    if (copy) {
      copy.textContent =
        orphans.length === 1
          ? 'An embossing service with no matching book was removed from your cart.'
          : `${orphans.length} embossing services with no matching book were removed from your cart.`;
    }

    let html = '<ul style="margin: 1rem 0; padding-left: 1.5rem;">';
    orphans.forEach((service) => {
      const language = (service.properties && service.properties.Language) || 'Unknown book';
      const text = (service.properties && service.properties.Text) || 'No text';
      html += `<li><strong>${language}</strong> — "${text}"</li>`;
    });
    html += '</ul>';
    servicesList.innerHTML = html;

    const removeButton = document.getElementById('remove-orphaned-services-drawer');
    if (removeButton) removeButton.style.display = 'none';

    banner.style.display = 'block';
    window.setTimeout(() => {
      banner.style.display = 'none';
    }, 6000);
  }

  function hideBanner() {
    const banner = document.getElementById('embossing-warning-banner-drawer');
    if (banner) banner.style.display = 'none';
  }

  async function purgeOrphanedEmbossingServices() {
    if (purgeInFlight) return { removed: 0 };
    purgeInFlight = true;
    try {
      const cart = await fetchCart();
      const orphans = findOrphanedServices(cart);
      if (!orphans.length) {
        hideBanner();
        return { removed: 0, orphans: [] };
      }

      console.info(
        '[embossing-cart-guard] Removing orphaned embossing services:',
        orphans.map((item) => ({
          key: item.key,
          pair_id: getEmbossingId(item),
          text: item.properties && item.properties.Text,
        }))
      );

      await removeLineKeys(orphans.map((item) => item.key));
      showRemovedNotice(orphans);
      refreshCartUI();
      return { removed: orphans.length, orphans };
    } catch (error) {
      console.error('[embossing-cart-guard] Failed to purge orphans:', error);
      return { removed: 0, orphans: [], error };
    } finally {
      purgeInFlight = false;
    }
  }

  function schedulePurge(delayMs) {
    window.setTimeout(() => {
      purgeOrphanedEmbossingServices();
    }, delayMs);
  }

  function isCheckoutControl(element) {
    if (!element || !(element instanceof Element)) return false;
    if (element.matches('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]')) {
      return true;
    }
    return Boolean(element.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]'));
  }

  async function gateCheckout(event) {
    if (checkoutGateInFlight) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const control = event.target && event.target.closest
      ? event.target.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]')
      : null;
    if (!control && !isCheckoutControl(event.target)) return;

    event.preventDefault();
    event.stopPropagation();
    checkoutGateInFlight = true;

    const button = control || event.target;
    if (button && 'disabled' in button) button.disabled = true;

    try {
      await purgeOrphanedEmbossingServices();
      const form = (button && button.form) || (button && button.closest && button.closest('form'));
      if (form) {
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '1';
        form.appendChild(checkoutInput);
        form.submit();
        return;
      }
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[embossing-cart-guard] Checkout gate failed:', error);
      window.location.href = '/checkout';
    } finally {
      checkoutGateInFlight = false;
      if (button && 'disabled' in button) button.disabled = false;
    }
  }

  function init() {
    // Catch orphans left in abandoned carts as soon as the storefront loads.
    schedulePurge(0);

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('cart-drawer:opened', () => schedulePurge(0));
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'embossing-cart-guard') return;
        schedulePurge(450);
      });
    }

    document.addEventListener('cart-update', () => schedulePurge(450));
    document.addEventListener('quantity-update', () => schedulePurge(450));

    // After a book/set line is removed (drawer or cart page), re-check pairing.
    document.addEventListener(
      'click',
      (event) => {
        const removeButton = event.target.closest('cart-remove-button');
        if (!removeButton) return;
        if (removeButton.dataset.isEmbossing === 'true') return;
        if (!removeButton.dataset.embossingId) return;
        schedulePurge(650);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!isCheckoutControl(event.target)) return;
        gateCheckout(event);
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.NativeScriptureEmbossingCartGuard = {
    purgeOrphanedEmbossingServices,
    findOrphanedServices,
  };
})();
